'use client';

import { type FC } from 'react';
import { motion, type Variants } from 'framer-motion';
import clsx from 'clsx';

type GalleryVideoSource = {
  src: string;
  type?: string;
};

type GalleryImage = {
  src: string;
  alt: string;
  caption?: string;
  label?: string;
  kind?: 'image' | 'video';
  sources?: GalleryVideoSource[];
  poster?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
};

type GalleryLayout = 'single' | 'double' | 'triple' | 'quad';

interface MasonryGalleryProps {
  images: GalleryImage[];
  className?: string;
  layout: GalleryLayout;
  onImageClick?: (image: GalleryImage) => void;
}

const PRIMARY_CLIP_PATH =
  "path('M100% 94.350% C100% 94.780% 99.810% 95.210% 99.480% 95.520% L95.210% 99.510% C94.880% 99.830% 94.430% 100% 93.960% 100% H5.310% C4.330% 100% 3.540% 99.260% 3.540% 98.340% V18.910% C3.540% 18.470% 3.350% 18.040% 3.020% 17.730% L0.520% 15.390% C0.190% 15.080% 0% 14.660% 0% 14.220% V3.310% C0% 2.400% 0.790% 1.660% 1.770% 1.660% H86.880% C87.350% 1.660% 87.800% 1.480% 88.130% 1.170% L88.860% 0.490% C89.190% 0.170% 89.640% 0% 90.110% 0% H98.230% C99.210% 0% 100% 0.740% 100% 1.660% V94.350% Z')";

const SECONDARY_CLIP_PATH =
  "path('M14.970% 0.000% C15.681% 0.000% 16.363% 0.356% 16.865% 0.989% L17.976% 2.387% C18.478% 3.020% 19.160% 3.376% 19.871% 3.376% H97.320% C98.800% 3.376% 100.000% 4.887% 100.000% 6.751% V28.982% C100.000% 29.877% 99.718% 30.735% 99.215% 31.368% L95.425% 36.142% C94.922% 36.775% 94.640% 37.634% 94.640% 38.529% V88.475% C94.640% 89.370% 94.357% 90.229% 93.855% 90.862% L87.385% 99.011% C86.882% 99.644% 86.200% 100.000% 85.490% 100.000% H2.680% C1.200% 100.000% 0.000% 98.489% 0.000% 96.624% V3.376% C0.000% 1.511% 1.200% 0.000% 2.680% 0.000% H14.970% Z')";

const PRIMARY_MASK_IMAGE =
  'url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20904%20966%22%20preserveAspectRatio%3D%22none%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22M904%20911.373C904%20915.616%20902.314%20919.686%20899.314%20922.686L860.686%20961.314C857.686%20964.314%20853.616%20966%20849.373%20966H48C39.1634%20966%2032%20958.837%2032%20950V182.627C32%20178.384%2030.3143%20174.314%2027.3137%20171.314L4.68629%20148.686C1.6857%20145.686%200%20141.616%200%20137.373V32C0%2023.1634%207.16344%2016%2016%2016H785.373C789.616%2016%20793.686%2014.3143%20796.686%2011.3137L803.314%204.68629C806.314%201.6857%20810.384%200%20814.627%200H888C896.837%200%20904%207.16344%20904%2016V911.373Z%22%2F%3E%3C%2Fsvg%3E")';

const SECONDARY_MASK_IMAGE =
  'url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20597%20474%22%20preserveAspectRatio%3D%22none%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22M89.3726%200C93.616%200%2097.6857%201.68571%20100.686%204.68629L107.314%2011.3137C110.314%2014.3143%20114.384%2016%20118.627%2016H581C589.837%2016%20597%2023.1634%20597%2032V137.373C597%20141.616%20595.314%20145.686%20592.314%20148.686L569.686%20171.314C566.686%20174.314%20565%20178.384%20565%20182.627V419.373C565%20423.616%20563.314%20427.686%20560.314%20430.686L521.686%20469.314C518.686%20472.314%20514.616%20474%20510.373%20474H16C7.16346%20474%200%20466.837%200%20458V16C0%207.16346%207.16344%200%2016%200H89.3726Z%22%2F%3E%3C%2Fsvg%3E")';

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.19, 1, 0.22, 1],
      staggerChildren: 0.14,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.19, 1, 0.22, 1],
    },
  },
};

const LAYOUT_CONFIG: Record<
  GalleryLayout,
  {
    itemClasses: string[];
    gridClass: string;
    clipSequence: ('primary' | 'secondary')[];
  }
> = {
  single: {
    itemClasses: ['col-span-full md:col-span-12 aspect-[16/9]'],
    gridClass: 'grid-cols-1 md:grid-cols-12 md:grid-rows-[auto] md:items-stretch',
    clipSequence: ['primary'],
  },
  double: {
    itemClasses: [
      'col-span-full md:col-span-6 aspect-[4/3]',
      'col-span-full md:col-span-6 aspect-[4/3]',
    ],
    gridClass: 'grid-cols-1 md:grid-cols-12 md:items-stretch',
    clipSequence: ['primary', 'secondary'],
  },
  triple: {
    itemClasses: [
      'col-span-full md:col-span-4 aspect-[3/4]',
      'col-span-full md:col-span-4 aspect-[3/4]',
      'col-span-full md:col-span-4 aspect-[3/4]',
    ],
    gridClass: 'grid-cols-1 md:grid-cols-12 md:items-stretch',
    clipSequence: ['primary', 'secondary', 'primary'],
  },
  quad: {
    itemClasses: [
      'col-span-full md:col-span-3 aspect-[4/5]',
      'col-span-full md:col-span-3 aspect-[4/5]',
      'col-span-full md:col-span-3 aspect-[4/5]',
      'col-span-full md:col-span-3 aspect-[4/5]',
    ],
    gridClass: 'grid-cols-1 md:grid-cols-12 md:items-stretch',
    clipSequence: ['primary', 'secondary', 'secondary', 'primary'],
  },
};

const CLIP_LOOKUP = {
  primary: {
    clipPath: PRIMARY_CLIP_PATH,
    maskImage: PRIMARY_MASK_IMAGE,
  },
  secondary: {
    clipPath: SECONDARY_CLIP_PATH,
    maskImage: SECONDARY_MASK_IMAGE,
  },
} as const;

const MasonryGalleryBase: FC<MasonryGalleryProps> = ({ className, images, layout, onImageClick }) => {
  const { itemClasses, gridClass, clipSequence } = LAYOUT_CONFIG[layout];
  const imageCount = Math.min(images.length, itemClasses.length);
  const hasMismatch = images.length !== itemClasses.length;

  if (hasMismatch && process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    console.warn(
      `[MasonryGallery] Expected ${itemClasses.length} images for layout "${layout}" but received ${images.length}. Extra items will be ignored.`,
    );
  }

  return (
    <div className="relative">
      <motion.div
        className={clsx('grid gap-6 md:gap-12', gridClass, className)}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        {images.slice(0, imageCount).map((image, index) => {
          const clipKey = clipSequence[index] ?? 'primary';
          const clip = CLIP_LOOKUP[clipKey];

          const isVideo =
            image.kind === 'video' ||
            Boolean(image.sources?.some((source) => source.type?.toLowerCase().includes('video'))) ||
            image.src.toLowerCase().endsWith('.mp4') ||
            image.src.toLowerCase().endsWith('.webm') ||
            image.src.toLowerCase().endsWith('.mov');

        const handleClick = () => {
          if (!isVideo && onImageClick) {
            onImageClick(image);
          }
        };

        const isInteractive = Boolean(onImageClick) && !isVideo;

        return (
          <motion.figure
            key={index}
            style={{
                clipPath: clip.clipPath,
                WebkitClipPath: clip.clipPath,
                maskImage: clip.maskImage,
                WebkitMaskImage: clip.maskImage,
                maskSize: '100% 100%',
                WebkitMaskSize: '100% 100%',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                backgroundColor: 'transparent',
              }}
              variants={itemVariants}
              className={clsx(
                'group relative isolate flex flex-col overflow-hidden bg-transparent',
                isInteractive ? 'cursor-zoom-in' : null,
                itemClasses[index],
              )}
              onClick={isInteractive ? handleClick : undefined}
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleClick();
                      }
                    }
                  : undefined
              }
              role={isInteractive ? 'button' : undefined}
              tabIndex={isInteractive ? 0 : undefined}
          >
              <div className="absolute inset-0 overflow-hidden bg-transparent">
                {isVideo ? (
                  <video
                    className="size-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                    autoPlay={image.autoPlay ?? true}
                    loop={image.loop ?? true}
                    muted={image.muted ?? true}
                    playsInline={image.playsInline ?? true}
                    poster={image.poster}
                    aria-label={image.alt}
                  >
                    {(image.sources && image.sources.length > 0 ? image.sources : [{ src: image.src }]).map(
                      ({ src, type }) => (
                        <source key={src} src={src} type={type} />
                      ),
                    )}
                  </video>
                ) : (
                  <img
                    alt={image.alt}
                    className="size-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                    decoding="async"
                    loading="lazy"
                    src={image.src}
                  />
                )}
              </div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-neutral-950/80 via-neutral-950/40 to-transparent opacity-90 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-95"
              />
              {(image.caption || image.label) && (
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] px-6 py-4 text-sm font-light text-white/90 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                  <div className="flex flex-col gap-1">
                    {image.caption ? <span>{image.caption}</span> : null}
                    {image.label ? (
                      <span className="text-xs uppercase tracking-[0.3em] text-white/60">{image.label}</span>
                    ) : null}
                  </div>
                </figcaption>
              )}
            </motion.figure>
          );
        })}
      </motion.div>
    </div>
  );
};

export const MasonryGallerySingle: FC<Omit<MasonryGalleryProps, 'layout'>> = (props) => (
  <MasonryGalleryBase layout="single" {...props} />
);

export const MasonryGalleryDouble: FC<Omit<MasonryGalleryProps, 'layout'>> = (props) => (
  <MasonryGalleryBase layout="double" {...props} />
);

export const MasonryGalleryTriple: FC<Omit<MasonryGalleryProps, 'layout'>> = (props) => (
  <MasonryGalleryBase layout="triple" {...props} />
);

export const MasonryGalleryQuad: FC<Omit<MasonryGalleryProps, 'layout'>> = (props) => (
  <MasonryGalleryBase layout="quad" {...props} />
);

export type { GalleryImage as MasonryGalleryImage, MasonryGalleryProps };
